import { action, set } from '@ember/object';
import Route from '@ember/routing/route';
import { service } from '@ember/service';
import { pushAndReturnPayload } from 'frontend-utils/utils/ember-data';
import {
  DATE_AGGREGATION,
  FILTER_BAR_PAGETYPES,
} from 'lumen/utils/constants/general-constants';
import {
  ACTIVE_FILTERS,
  DEFAULT_DAYS_SHOWN_IN_OVERVIEW,
  DEMAND_FORECAST_SERIES_VISIBILITY_KEY,
} from 'lumen/utils/constants/market-insight-constants';
import { parseDateRangeString, parseObject } from 'lumen/utils/parse-utils';
import { formatUrl } from 'lumen/utils/rates-request';
import { hash } from 'rsvp';

export default class HotelMarketInsightRoute extends Route {
  queryParams = {
    selectedCalendarViewFilterIds: {
      refreshModel: false,
    },
    focusedDetailSection: {
      refreshModel: false,
    },
    activeFilters: {
      refreshModel: true,
    },
    extractDateIndex: {
      refreshModel: false,
    },
    showAllEvents: {
      refreshModel: false,
    },
    evolutionType: {
      refreshModel: false,
    },
    fromDate: {
      refreshModel: true,
    },
    toDate: {
      refreshModel: true,
    },
    searchesDetailsItem: {
      refreshModel: false,
    },
    dateAggregation: {
      refreshModel: true,
    },
    refDaysAgo: {
      refreshModel: true,
    },
    scrollTo: {
      refreshModel: false,
    },
    isSearchesCardExpanded: {
      refreshModel: false,
    },
  };

  @service access;
  @service ajax;
  @service alerting;
  @service errorTracking;
  @service('hotel-and-market') hotelAndMarketService;
  @service miAlternativeLodging;
  @service miDemand;
  @service miHotels;
  @service miSearches;
  @service miSelections;
  @service router;
  @service sessionStorage;
  @service store;
  @service transition;

  gridExtractDateFromInitialRequest = '';
  hasDoneInitialRequests = false;
  lastOverviewRangeEnd = null;
  lastOverviewRangeStart = null;
  lastOverviewResponse = null;
  lastOverviewResponseByDay = null;
  lastOverviewResponseByWeek = null;
  lastReturnedGeoData = null;
  lastReturnedGridFeatures = null;
  lastSelectedDay = null;
  lastSelectedRefPeriod = null;
  requestedFavourites = null;

  async beforeModel(transition) {
    if (this.access.isMarketInsightMarketUnsupported) {
      await this.router.transitionTo('hotel.market-insight-unsupported-market');
      return;
    }

    if (!this.access.hasMarketInsight) {
      await this.transition.redirectToEnabledTab(transition);
      return;
    }

    await this.transition.validateMarketInsightQueryParams(transition);

    // calculate and set the correct reference date values in the MI Selections service, because we can use these to generate API request parameters more easily.
    this.miSelections.setRefDaysAgoDatesInAPIFormat();
  }

  clearCachedData() {
    /**
     * In application tests, the app gets torn down after each test. This causes the store to be destroyed
     * before the deactivate hook is called. This check is to prevent the store from being accessed after it's destroyed.
     */
    if (!this.store.isDestroyed) {
      // Prevent having any data from previous hotels sticking around
      this.store.unloadAll('rateindex');
      this.store.unloadAll('outlier');
      this.store.unloadAll('levelbucket');
      this.store.unloadAll('summary');
      this.store.unloadAll('searchsummary');
      this.store.unloadAll('countrysearch');
      this.store.unloadAll('lossearch');
      this.store.unloadAll('event');
      this.store.unloadAll('holiday');
      // unload for occupancy and day are commented because it was causing issues on route transition (specific going to Short-Term Rental upsell page)
      // the problem was that those two models weren't filled with the data of the target route after the unload
      // this.store.unloadAll('occupancy');
      // this.store.unloadAll('day');
    }

    // Clear the stored values in the mi-hotels, mi-earches & mi-alternative-lodging service
    this.miHotels.clearValues();
    this.miSearches.clearValues();
    this.miAlternativeLodging.clearValues();
    this.miDemand.clearValues();
  }

  clearRouteStateValues() {
    this.lastSelectedDay = null;
    this.lastOverviewResponse = null;
    this.lastOverviewResponseByDay = null;
    this.lastOverviewResponseByWeek = null;
    this.lastReturnedGridFeatures = null;
    this.lastReturnedGeoData = null;
    this.lastSelectedRefPeriod = null;
    this.hasDoneInitialRequests = false;
    this.gridExtractDateFromInitialRequest = '';
    this.requestedFavourites = '';
    this.sessionStorage.removeItem(DEMAND_FORECAST_SERIES_VISIBILITY_KEY);
  }

  // Error handling function that is called in the catch function of PM API requests. Logs error to Sentry & shows alert with an error message
  handleRequestError(error) {
    this.errorTracking.captureException(error);
    this.alerting.showErrorAlert();
  }

  model(params) {
    const { activeFilters, fromDate, toDate, dateAggregation, refDaysAgo } =
      params;

    const parsedActiveFilters = activeFilters && parseObject(activeFilters);
    const selectedDay =
      parsedActiveFilters && parsedActiveFilters[ACTIVE_FILTERS.DAY];

    // Use the DatePicker range by default for the Overview request
    let overviewRangeStart = null;
    let overviewRangeEnd = null;

    if (fromDate) {
      overviewRangeStart = fromDate;
    } else {
      overviewRangeStart = this.hotelAndMarketService
        .getLocalTime()
        .format('YYYY-MM-DD');
    }

    if (toDate) {
      overviewRangeEnd = toDate;
    } else {
      overviewRangeEnd = this.hotelAndMarketService
        .getLocalTime()
        .add(DEFAULT_DAYS_SHOWN_IN_OVERVIEW, 'day')
        .format('YYYY-MM-DD');
    }

    // If there's a range filter: use this as the range for the Overview request
    if (parsedActiveFilters[ACTIVE_FILTERS.RANGE]) {
      const selectedRange = parseDateRangeString(
        parsedActiveFilters[ACTIVE_FILTERS.RANGE],
      );
      overviewRangeStart = selectedRange.start;
      overviewRangeEnd = selectedRange.end;
    }

    const subscriptionId = this.hotelAndMarketService.hotelOrMarket.id;

    const modelResponse = {
      calendar: {
        isLoading: false,
      },
      detail: {
        isLoading: false,
      },
      overview: {
        isLoading: false,
        data: {
          occupancies: this.lastOverviewResponse?.occupancies ?? [],
          summaries: this.lastOverviewResponse?.summaries ?? [],
          searchsummaries: this.lastOverviewResponse?.searchsummaries ?? [],
        },
      },
      map: {
        isLoading: false,
        data: {
          geoData: this.lastReturnedGeoData ?? {},
          gridFeatures: this.lastReturnedGridFeatures || {},
        },
      },
    };

    if (!this.requestedFavourites) {
      const requestFavourites = {
        hotel: this.hotelAndMarketService.hotel?.id,
        page_type: FILTER_BAR_PAGETYPES.MARKET_INSIGHT,
      };

      this.store
        .query('insightpagesfavourite', requestFavourites)
        .then((content) => {
          set(this, 'requestedFavourites', [...content]);
        });
    }

    // If there is a selectedDay QP: we are looking at the detail view
    const isDetailView = selectedDay;

    const isSelectedDayUpdate =
      isDetailView && selectedDay !== this.lastSelectedDay;
    const isSelectedRangeUpdate =
      overviewRangeStart !== this.lastOverviewRangeStart ||
      overviewRangeEnd !== this.lastOverviewRangeEnd;
    if (isSelectedRangeUpdate) {
      // Clear the aggregated overview responses when the range is updated
      this.lastOverviewResponseByDay = null;
      this.lastOverviewResponseByWeek = null;
    }
    const isSelectedRefPeriodUpdate = refDaysAgo !== this.lastSelectedRefPeriod;

    const requiresExtraDayRequest =
      dateAggregation === DATE_AGGREGATION.DAY &&
      this.lastOverviewResponseByDay === null;
    const requiresExtraWeekRequest =
      dateAggregation === DATE_AGGREGATION.WEEK &&
      this.lastOverviewResponseByWeek === null;
    const requiresExtraOverviewRequest =
      requiresExtraDayRequest || requiresExtraWeekRequest;

    const requestsToHandle = {};

    // Prepare the extract date parameters (required for summaries & overview request)
    // Expected format is e.g. extract_dates[]=1970-01-01&extract_dates[]=2022-05-04
    // P.S.: 1970-01-01 is to get the latest data
    const extractDateURLParameters = {
      'extract_dates[]': [
        // Backend uses this value as extract date to indicate "latest" data
        '1970-01-01',
        this.miSelections.refDaysAgoDatesInAPIFormat[refDaysAgo],
      ],
    };

    // Do the initial requests when loading MI for the first time
    if (!this.hasDoneInitialRequests) {
      // The initial request for the SidebarCalendar
      const summariesRequestPath = '/apigateway/v1/app/marketinsight/summaries';
      const summariesRequestParams = {
        ...extractDateURLParameters,
        subscription_id: subscriptionId,
      };

      requestsToHandle.summariesRequest = this.ajax.request(
        formatUrl(summariesRequestPath, summariesRequestParams, this.ajax.host),
      );
      set(modelResponse, 'calendar.isLoading', true);

      // The initial request for the CityMap
      requestsToHandle.gridRequest = this.ajax.request(
        `${this.ajax.host}/apigateway/v1/app/marketinsight/grid?subscription_id=${subscriptionId}`,
      );
      set(modelResponse, 'map.isLoading', true);

      if (isDetailView) {
        // If the selectedDay param is present: we need to do a detail request and go straight to the map view
        const detailRequestPath = '/apigateway/v1/app/marketinsight/detail';
        const detailRequestParams = {
          subscription_id: subscriptionId,
          fromdate: selectedDay,
        };

        // Secret URL parameter (not used in the UI - but mainly for debugging/research purposes)
        if (params.evolutionType === 'day') {
          detailRequestParams.evolution_type = 'day';
        }

        requestsToHandle.detailRequest = this.ajax.request(
          formatUrl(detailRequestPath, detailRequestParams, this.ajax.host),
        );
        set(modelResponse, 'detail.isLoading', true);
      } else {
        // If the selectedDay param is not present: we need to do the MI Overview requests
        const overviewRequestPath = '/apigateway/v1/app/marketinsight/overview';
        const overviewRequestParams = {
          ...extractDateURLParameters,
          subscription_id: subscriptionId,
          fromdate: overviewRangeStart,
          todate: overviewRangeEnd,
          date_aggregation: dateAggregation,
        };

        requestsToHandle.overviewRequest = this.ajax.request(
          formatUrl(overviewRequestPath, overviewRequestParams, this.ajax.host),
        );
        set(modelResponse, 'overview.isLoading', true);
      }
    } else if (isSelectedDayUpdate) {
      // Do a new detail request when an other day has been selected
      const detailRequestPath = '/apigateway/v1/app/marketinsight/detail';
      const detailRequestParams = {
        subscription_id: subscriptionId,
        fromdate: selectedDay,
      };

      // We need to pass this parameter if this one is stored. API needs this to avoid potential issues with the grid
      if (this.gridExtractDateFromInitialRequest) {
        detailRequestParams.map_extract_date =
          this.gridExtractDateFromInitialRequest;
      }

      // Secret URL parameter (not used in the UI - but mainly for debugging/research purposes)
      if (params.evolutionType === 'day') {
        detailRequestParams.evolution_type = 'day';
      }

      requestsToHandle.detailRequest = this.ajax.request(
        formatUrl(detailRequestPath, detailRequestParams, this.ajax.host),
      );
      set(modelResponse, 'detail.isLoading', true);
    } else if (isSelectedRefPeriodUpdate) {
      // If only the ref period has been updated (via the dropdown in the blue navbar), we need to redo the summaries & overview request
      // Both requests will be impacted because they look at the extract_dates[] parameters

      // Note: the new API allows to only request the new ref date data, but that sadly doesn't work within our current route structure.
      // We already have the latest data in our store, but we can't access that easily & in our route we always work directly with the latest responses
      // This scenario could be optimised, but would require a route/data structure refactor. Probably would mean a separate request for latest & ref data for the overview & summaries
      const summariesRequestPath = '/apigateway/v1/app/marketinsight/summaries';
      const summariesRequestParams = {
        ...extractDateURLParameters,
        subscription_id: subscriptionId,
      };

      requestsToHandle.summariesRequest = this.ajax.request(
        formatUrl(summariesRequestPath, summariesRequestParams, this.ajax.host),
      );
      set(modelResponse, 'calendar.isLoading', true);

      const overviewRequestPath = '/apigateway/v1/app/marketinsight/overview';
      const overviewRequestParams = {
        ...extractDateURLParameters,
        subscription_id: subscriptionId,
        fromdate: overviewRangeStart,
        todate: overviewRangeEnd,
        date_aggregation: dateAggregation,
      };

      requestsToHandle.overviewRequest = this.ajax.request(
        formatUrl(overviewRequestPath, overviewRequestParams, this.ajax.host),
      );
      set(modelResponse, 'overview.isLoading', true);
    } else if (isSelectedRangeUpdate || requiresExtraOverviewRequest) {
      // Do new overview request
      const overviewRequestPath = '/apigateway/v1/app/marketinsight/overview';
      const overviewRequestParams = {
        ...extractDateURLParameters,
        subscription_id: subscriptionId,
        fromdate: overviewRangeStart,
        todate: overviewRangeEnd,
        date_aggregation: dateAggregation,
      };

      requestsToHandle.overviewRequest = this.ajax.request(
        formatUrl(overviewRequestPath, overviewRequestParams, this.ajax.host),
      );
      set(modelResponse, 'overview.isLoading', true);
    } else if (!requiresExtraOverviewRequest) {
      // We make sure to always return the correct overviewResponse according to the current dateAggregation QP (= no extra request needs be triggered when we have it in cache)
      if (dateAggregation === DATE_AGGREGATION.DAY) {
        this.lastOverviewResponse = this.lastOverviewResponseByDay;
      } else if (dateAggregation === DATE_AGGREGATION.WEEK) {
        this.lastOverviewResponse = this.lastOverviewResponseByWeek;
      }
      set(
        modelResponse,
        'overview.data.occupancies',
        this.lastOverviewResponse.occupancies,
      );
      set(
        modelResponse,
        'overview.data.summaries',
        this.lastOverviewResponse.summaries,
      );
      set(
        modelResponse,
        'overview.data.searchsummaries',
        this.lastOverviewResponse.searchsummaries,
      );
    }

    hash(requestsToHandle)
      .then((hashResult) => {
        // Set this flag to true once we know that the first request has been completely finished
        // Should contain the summaries & grid info, so that we can show everything in the product
        this.hasDoneInitialRequests = true;

        if (hashResult.summariesRequest) {
          // Ember data 4.7 crashes when the API response returns `null`. It expects an empty array `[]` or no key at all if there is no data.
          hashResult.summariesRequest.occupancies =
            hashResult.summariesRequest.occupancies ?? [];
          // Don't push keys for which there is no model
          const summariesRequestToPush = { ...hashResult.summariesRequest };
          delete summariesRequestToPush.errors;
          this.store.pushPayload(summariesRequestToPush);

          this.lastSelectedRefPeriod = refDaysAgo;

          set(modelResponse, 'calendar.isLoading', false);
        }

        if (hashResult.detailRequest) {
          this.lastSelectedDay = selectedDay;

          // Ember data 4.7 crashes when the API response returns `null`. It expects an empty array `[]` or no key at all if there is no data.
          Object.keys(hashResult.detailRequest).forEach((key) => {
            hashResult.detailRequest[key] = hashResult.detailRequest[key] ?? [];
          });

          // Don't push keys for which there is no model
          const detailRequestToPush = { ...hashResult.detailRequest };
          delete detailRequestToPush.geodata;
          delete detailRequestToPush.errors;
          this.store.pushPayload(detailRequestToPush);

          set(
            modelResponse,
            'map.data.geoData',
            hashResult.detailRequest.geodata,
          );

          set(this, 'lastReturnedGeoData', hashResult.detailRequest.geodata);

          set(modelResponse, 'detail.isLoading', false);
          this.shouldWaitToRenderEventMarkers = false;
        }

        if (hashResult.gridRequest) {
          // We won't put this data in the store, because it would have too much performance impact & it has no added value to do so
          set(
            modelResponse,
            'map.data.gridFeatures',
            hashResult.gridRequest.grid,
          );

          // Store the grid features in the route
          this.lastReturnedGridFeatures = hashResult.gridRequest.grid;

          // hashResult.gridRequest.hotels response will also be stored in the mi-hotels service
          this.miHotels.createMapping(hashResult.gridRequest.hotels);

          this.miSearches.setSupportedSearchTypesForCurrentDestination(
            hashResult.gridRequest.supported_search_types,
          );

          this.miAlternativeLodging.setSupportedTypesForCurrentDestination(
            hashResult.gridRequest.supported_alternative_lodging_types,
          );

          this.miDemand.setDemandChangeThreshold(
            hashResult.gridRequest.demand_change_threshold,
          );

          set(modelResponse, 'map.isLoading', false);

          // Store this parameter because we need to pass this back to the API if further detail requests are fired
          if (hashResult.gridRequest.extract_date) {
            this.gridExtractDateFromInitialRequest =
              hashResult.gridRequest.extract_date;
          }
        }

        if (hashResult.overviewRequest) {
          const overviewResponse = pushAndReturnPayload(
            this.store,
            hashResult.overviewRequest,
          );

          this.lastOverviewRangeStart = overviewRangeStart;
          this.lastOverviewRangeEnd = overviewRangeEnd;

          this.lastOverviewResponse = overviewResponse;
          if (dateAggregation === DATE_AGGREGATION.DAY) {
            this.lastOverviewResponseByDay = overviewResponse;
          } else if (dateAggregation === DATE_AGGREGATION.WEEK) {
            this.lastOverviewResponseByWeek = overviewResponse;
          }

          set(
            modelResponse,
            'overview.data.occupancies',
            overviewResponse.occupancies,
          );
          set(
            modelResponse,
            'overview.data.summaries',
            overviewResponse.summaries,
          );
          set(
            modelResponse,
            'overview.data.searchsummaries',
            overviewResponse.searchsummaries,
          );
          set(modelResponse, 'overview.isLoading', false);
        }
      })
      .catch((error) => {
        set(modelResponse, 'calendar.isLoading', false);
        set(modelResponse, 'detail.isLoading', false);
        set(modelResponse, 'map.isLoading', false);
        set(modelResponse, 'overview.isLoading', false);

        this.handleRequestError(error);
      });

    return modelResponse;
  }

  resetController(controller, isExiting) {
    if (isExiting) {
      this.clearCachedData();
      this.clearRouteStateValues();

      if (controller.perfectScrollbarMainSidebarInstance) {
        controller.perfectScrollbarMainSidebarInstance.destroy();
        set(controller, 'perfectScrollbarMainSidebarInstance', null);
      }

      // Reset this flag on the controller so that we add the resize event handler again if the user would revisit MI
      set(controller, 'hasAddedOnWindowResizeEventHandler', false);

      set(controller, 'extractDateIndex', 0);
      set(controller, 'selectedCalendarViewFilterIds', []);
      set(controller, 'focusedDetailSection', '');
      set(controller, 'isShowingMIRatePreferenceModal', false);
      set(controller, 'hasInitialisedMiSelections', false);

      window.removeEventListener(
        'resize',
        controller.privateMarketInsightWindowResizeHandler,
      );
    }
  }

  @action
  refreshRoute() {
    this.clearCachedData();
    this.clearRouteStateValues();

    this.refresh();
  }
}
